<script setup lang="ts">
import { onMounted, ref, computed, watch, nextTick, onUnmounted } from "vue";
import {
  SearchMd,
  Clipboard,
  ChevronDown,
  Pencil02,
  LinkExternal02,
  InfoCircle,
  X,
} from "utruckin-icons";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import CustomCard from "@/components/custom/CustomCard.vue";
import { useTaskStore } from "@/features/tasks/task.store";
import { INewTask, ITask, ITaskData } from "@/features/tasks/task.types";
import CommonStatus from "@/components/common/CommonStatus.vue";
import { TaskStatus } from "@/features/tasks/task.enum";
import { DateTime } from "luxon";
import { useUserStore } from "@/features/users/user.store";
import { useNotification } from "@/utils/notification";
import { IDropdown } from "@/types/main";
import { useAppStore } from "@/store/app.store";
import { ICompany } from "@/features/company/company.types";
import CustomTableDropdown from "@/components/custom/CustomTableDropdown.vue";
import TaskAdditionalWorkTypes from "@/components/custom/TaskAdditionalWorkTypes.vue";
import { taskService } from "@/features/tasks/task.service";
import { IDriver } from "@/features/drivers/drivers.types";
import { IWorkType } from "@/features/work-types/work-type.types";
import { useDriverStore } from "@/features/drivers/drivers.store";
import { useCompanyStore } from "@/features/company/company.store";
import { useWorkTypeStore } from "@/features/work-types/work-type.store";
import { useKpiStore } from "@/features/kpi/kpi.store";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import { useArchiveStore } from "@/features/archive/archive.store";
import { companyService } from "@/features/company/company.service";
import { driverService } from "@/features/drivers/drivers.service";
import { workTypeService } from "@/features/work-types/work-type.service";
import CustomTab from "@/components/custom/CustomTab.vue";
import { kpiService } from "@/features/kpi/kpi.service";

const taskStore = useTaskStore();
const userStore = useUserStore();
const appStore = useAppStore();
const driverStore = useDriverStore();
const companyStore = useCompanyStore();
const workTypeStore = useWorkTypeStore();
const kpiStore = useKpiStore();
const archiveStore = useArchiveStore();

const { pushNotification } = useNotification();

const loading = ref(false);
const tasks = ref<ITaskData[]>([]);
const collapsedDates = ref<Record<string, boolean>>({});

const search = ref<string>("");
const status = ref<string>("");

const taskNumber = ref<number>(0);
const taskNumberLoading = ref<boolean>(false);
const isAdmin = ref<boolean>(false);

const companies = ref<ICompany[]>([]);
const drivers = ref<IDriver[]>([]);
const problems = ref<IWorkType[]>([]);
const additionalWorkTypes = ref<IWorkType[]>([]);

type PerformancePeriod = "today" | "month_to_date";

interface MyPerformanceStatistics {
  tasks: number;
  points: number;
  in_progress: number;
  on_hold: number;
  done: number;
  closed: number;
  qa_failed: number;
  closedRate: number;
}

const performancePeriod = ref<PerformancePeriod>("today");

const performanceTabs = [
  { label: "Today", value: "today" },
  { label: "Month", value: "month_to_date" },
];

const kpiStatistics = ref<MyPerformanceStatistics | null>(null);
let kpiRequestVersion = 0;

const companyLoading = ref(false);
const driverLoading = ref(false);
const problemLoading = ref(false);
const kpiLoading = ref(false);
const newTaskStatus = ref<string>("new");
const description = ref<string>("");
const newModal = ref(false);

const archiveModal = ref(false);
const archiveReason = ref<string>("");
const archiveModalItem = ref<ITaskData | null>(null);
const isSaving = ref<boolean>(false);

const newTask = ref<INewTask>({
  company_id: "",
  driver_id: "",
  work_type_id: "",
  priority: "",
  assignee_id: "",
});

// const company = ref<string>("");
// const driver = ref<string>("");
// const statusTask = ref<string>("");
// const workType = ref<string>("");

const dropdown1 = ref();
const dropdown2 = ref();
const dropdown3 = ref();

let searchTimer: ReturnType<typeof setTimeout> | null = null;

const PAGE_SIZE = 50;
const page = ref(1);
const hasMore = ref(true);
const isLoadingMore = ref(false);
const scrollContainer = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.NEW]: "--color-system-blue",
  [TaskStatus.INFORM]: "--color-system-grey",
  [TaskStatus.IN_PROGRESS]: "--color-system-orange",
  [TaskStatus.ON_HOLD]: "--color-system-yellow",
  [TaskStatus.DONE]: "--color-system-green",
  [TaskStatus.QA_FAILED]: "--color-system-red",
  [TaskStatus.CLOSED]: "--color-system-green",
};

const companyItems = computed(() =>
  (companies.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: c.provider_name,
  }))
);

const driverItems = computed(() =>
  (drivers.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: c.truck_number,
  }))
);

const problemItems = computed(() =>
  (problems.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: String(c.base_points),
  }))
);

const hasUserHasAction = (item: ITaskData) => {
  return (
    userStore.userProfile &&
    (userStore.userProfile.id == item.assignee_id ||
      userStore.userProfile.role == "super_admin" ||
      userStore.userProfile.role == "admin" ||
      userStore.userProfile.role == "supervisor")
  );
};

const formatStatusLabel = (key: string) =>
  key.replace(/_/g, "_").replace(/\b\w/g, (c) => c.toUpperCase());

const statusItems = (
  Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>
).map((key) => ({
  value: TaskStatus[key],
  name: formatStatusLabel(key),
  color: STATUS_COLORS[TaskStatus[key]],
}));

const summaryCards = [
  {
    label: "Total Tasks",
    name: "tasks",
  },
  {
    label: "KPI Points",
    name: "points",
  },
  {
    label: "In Progress",
    name: "in_progress",
    bg: "--color-orange-light",
  },
  {
    label: "On Hold",
    name: "on_hold",
    bg: "--color-yellow-light",
  },
  {
    label: "Done",
    name: "done",
    bg: "--color-green-light",
  },
  {
    label: "Closed",
    name: "closed",
    bg: "--color-green-light",
  },
  {
    label: "QA Failed",
    name: "qa_failed",
    bg: "--color-red-light",
  },
] as const;

const loadMyPerformance = async () => {
  const userId = userStore.userProfile?.id;
  if (!userId) return;

  const version = ++kpiRequestVersion;
  kpiLoading.value = true;

  try {
    const { data } = await kpiService.getMonthlyStatistics({
      assignee_id: userId,
      period: performancePeriod.value,
    });

    if (version !== kpiRequestVersion) return;

    const result = data.items?.[0];
    const counts = result?.status_counts ?? {};

    const totalTasks = Number(result?.total_tasks ?? 0);
    const closed = Number(counts.closed ?? 0);

    kpiStatistics.value = {
      tasks: totalTasks,
      points: Number(result?.total_points ?? 0),
      in_progress: Number(counts.in_progress ?? 0),
      on_hold: Number(counts.on_hold ?? 0),
      done: Number(counts.done ?? 0),
      closed,
      qa_failed: Number(counts.qa_failed ?? 0),
      closedRate: totalTasks > 0 ? Math.round((closed / totalTasks) * 100) : 0,
    };
  } catch (error: any) {
    if (version === kpiRequestVersion) {
      pushNotification(
        error.response?.data?.detail || "Failed to load performance statistics",
        "error"
      );
    }
  } finally {
    if (version === kpiRequestVersion) {
      kpiLoading.value = false;
    }
  }
};

watch(performancePeriod, () => {
  void loadMyPerformance();
});

const getStatusEnumKey = (qaStatus: string) => {
  const key = (Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>).find(
    (k) => TaskStatus[k] === qaStatus
  );
  return key ? formatStatusLabel(key) : null;
};

const toggleDate = (date: string) => {
  collapsedDates.value[date] = !collapsedDates.value[date];
};

const isDateCollapsed = (date: string) => collapsedDates.value[date];

const groupedByDate = computed(() => {
  const dateMap = new Map<string, ITaskData[]>();

  if (tasks.value && tasks.value.length) {
    for (const task of tasks.value) {
      const date = task.created_time
        ? DateTime.fromISO(task.created_time, { zone: "utc" })
            .setZone("America/New_York")
            .toFormat("yyyy-MM-dd")
        : "Unknown";

      if (!dateMap.has(date)) {
        dateMap.set(date, []);
      }

      dateMap.get(date)!.push(task);
    }
  }

  return Array.from(dateMap.entries()).map(([date, items]) => ({
    date,
    items,
  }));
});

function datepickerToUtcDateOnly(d: string | Date | null | undefined) {
  if (!d) return "";

  const date =
    typeof d === "string"
      ? DateTime.fromISO(d, { zone: "utc" })
      : DateTime.fromJSDate(d, { zone: "utc" });

  return date.setZone("America/New_York").toFormat("HH:mm:ss");
}

const getTaskNumber = async () => {
  const data = await taskStore.getAlertQueueCount(
    taskNumberLoading,
    userStore.userProfile?.team_id
  );
  taskNumber.value = data.count;
};

const getNewTask = async () => {
  const data = await taskStore.takeAlert(taskNumberLoading);
  if (data) {
    pushNotification("Successfully added", "success");
    window.location.reload();
  }
};

let taskFilterVersion = 0;

const fetchTasks = async (
  requestedPage: number,
  append: boolean,
  version: number
): Promise<boolean> => {
  const loadingRef = append ? isLoadingMore : loading;
  loadingRef.value = true;

  const filters = {
    ...taskStore.myTaskFilter,
    page: requestedPage,
    per_page: PAGE_SIZE,
  };

  try {
    const { data } = await taskService.getMyQueue(filters);

    if (version !== taskFilterVersion) {
      return false;
    }

    const items: ITaskData[] = data.items ?? [];

    tasks.value = append ? [...tasks.value, ...items] : items;

    page.value = requestedPage;
    taskStore.myTaskFilter.page = requestedPage;
    taskStore.myTaskFilter.per_page = PAGE_SIZE;

    hasMore.value =
      data.has_more !== undefined ? data.has_more : items.length === PAGE_SIZE;

    return true;
  } catch (error: any) {
    if (version === taskFilterVersion) {
      pushNotification(
        error.response?.data?.detail || "Failed to load tasks",
        "error"
      );

      if (append) {
        hasMore.value = false;
      }
    }

    return false;
  } finally {
    if (append) {
      isLoadingMore.value = false;
    } else if (version === taskFilterVersion) {
      loading.value = false;
    }
  }
};

const resetAndFetch = async () => {
  const version = ++taskFilterVersion;

  page.value = 1;
  hasMore.value = true;
  loading.value = true;

  await fetchTasks(1, false, version);
};

const loadMore = async () => {
  if (
    loading.value ||
    isLoadingMore.value ||
    searchTimer !== null ||
    !hasMore.value
  ) {
    return;
  }

  const version = taskFilterVersion;
  const nextPage = page.value + 1;

  await fetchTasks(nextPage, true, version);
};

let companySearchVersion = 0;
let driverSearchVersion = 0;
let problemSearchVersion = 0;

const fetchCompanyOptions = async (searchText = "") => {
  const version = ++companySearchVersion;

  try {
    const { data } = await companyService.getSimpleCompanies({
      ...companyStore.commonSimpleFilter,
      search: searchText.trim(),
      page: 1,
    });

    if (version !== companySearchVersion) return;

    companies.value = data.items ?? [];
  } catch (error) {
    if (version === companySearchVersion) {
      console.error("Failed to load companies", error);
    }
  }
};

const fetchDriverOptions = async (
  searchText = "",
  companyId = newTask.value.company_id
) => {
  const version = ++driverSearchVersion;

  if (!companyId || newTask.value.company_name) {
    drivers.value = [];
    return;
  }

  try {
    const { data } = await driverService.getSimpleDrivers({
      ...driverStore.commonSimpleFilter,
      search: searchText.trim(),
      company_id: companyId,
      page: 1,
    });

    if (
      version !== driverSearchVersion ||
      newTask.value.company_id !== companyId
    ) {
      return;
    }

    drivers.value = data.items ?? [];
  } catch (error) {
    if (version === driverSearchVersion) {
      console.error("Failed to load drivers", error);
    }
  }
};

const fetchProblemOptions = async (searchText = "") => {
  const version = ++problemSearchVersion;

  try {
    const { data } = await workTypeService.getWorkTypes({
      is_active: true,
      search: searchText.trim(),
    });

    if (version !== problemSearchVersion) return;

    problems.value = data;
  } catch (error) {
    if (version === problemSearchVersion) {
      console.error("Failed to load work types", error);
    }
  }
};

const getList = async () => {
  if (searchCompanyTimer) clearTimeout(searchCompanyTimer);
  if (searchDriverTimer) clearTimeout(searchDriverTimer);
  if (searchProblemTimer) clearTimeout(searchProblemTimer);

  searchCompanyTimer = null;
  searchDriverTimer = null;
  searchProblemTimer = null;

  companyStore.commonSimpleFilter.search = "";
  driverStore.commonSimpleFilter.search = "";
  workTypeStore.commonFilter.search = "";

  // Invalidate previous searches before reloading reference lists.
  companySearchVersion++;
  driverSearchVersion++;
  problemSearchVersion++;

  const companyVersion = companySearchVersion;
  const problemVersion = problemSearchVersion;

  // Invalidate pending driver requests for previous companies.
  drivers.value = [];

  const [companiesResponse, problemsResponse] = await Promise.all([
    companyService.getSimpleCompanies({
      ...companyStore.commonSimpleFilter,
      search: "",
      page: 1,
    }),
    workTypeService.getWorkTypes({
      is_active: true,
      search: "",
    }),
  ]);

  const fetchedCompanies = companiesResponse.data;
  const fetchedProblems = problemsResponse.data as IWorkType[];

  await resetAndFetch();

  await loadMyPerformance();

  if (companyVersion === companySearchVersion) {
    companies.value = fetchedCompanies.items;
  }

  if (problemVersion === problemSearchVersion) {
    problems.value = fetchedProblems; // All Tasks
  }

  additionalWorkTypes.value = fetchedProblems;

  newTask.value = {
    company_id: "",
    driver_id: "",
    work_type_id: "",
    priority: "",
    assignee_id: "",
  };

  await getTaskNumber();
};

const nextStatus = async (item: ITaskData) => {
  if (item.status == "new") {
    const data = await taskStore.startTask(loading, item.id);
    if (data) {
      pushNotification("Successfully changed", "success");
      await getList();
    }
  } else if (item.status == "in_progress") {
    const data = await taskStore.doneTask(loading, item.id);
    if (data) {
      pushNotification("Successfully changed", "success");
      await getList();
    }
  }
};

const confirmStatus = async (item: ITaskData) => {
  if (item.status == "in_progress") {
    const data = await taskStore.doneTask(loading, item.id);
    if (data) {
      pushNotification("Successfully changed", "success");
      await getList();
    }
  }
};

const formatToEdt = (date: any) => {
  if (!date) return "";

  return date.split("T")[1] == "11:00:00.000Z"
    ? DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d")
    : DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d, HH:mm ZZZZ");
};

const tooltipContent = (list: any[]) =>
  list
    ?.map((item) => `<p>${item.label} - ${formatToEdt(item.date)}</p>`)
    ?.join("");

const availableStatusItems = (item: ITaskData) => {
  let allStatus: any[] = [];

  if (item.status == "new") {
    allStatus = (
      Object.keys({
        IN_PROGRESS: "in_progress",
        ON_HOLD: "on_hold",
        DONE: "done",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  } else if (item.status == "in_progress") {
    allStatus = (
      Object.keys({
        ON_HOLD: "on_hold",
        DONE: "done",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  } else if (item.status == "on_hold") {
    allStatus = (
      Object.keys({
        IN_PROGRESS: "in_progress",
        DONE: "done",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  } else if (item.status == "qa_failed") {
    allStatus = (
      Object.keys({
        IN_PROGRESS: "in_progress",
        ON_HOLD: "on_hold",
        DONE: "done",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  }

  allStatus.push({
    value: "archive",
    name: "ARCHIVE",
    color: "",
  });

  return allStatus;
};

const selectStatus = async (item: ITaskData, status: IDropdown) => {
  if (item.status == "on_hold") {
    const data = await taskStore.resumeTask(loading, item.id);
    if (data) {
      if (status.value == "in_progress") {
        pushNotification("Successfully changed", "success");
        await getList();
      } else {
        const data = await taskStore.doneTask(loading, item.id);
        if (data) {
          pushNotification("Successfully changed", "success");
          await getList();
        }
      }
    }
  } else {
    if (status.value == "on_hold") {
      const data = await taskStore.holdTask(loading, item.id);
      if (data) {
        pushNotification("Successfully changed", "success");
        await getList();
      }
    } else if (status.value == "in_progress") {
      const data = await taskStore.startTask(loading, item.id);
      if (data) {
        pushNotification("Successfully changed", "success");
        await getList();
      }
    } else if (status.value == "done") {
      const data = await taskStore.doneTask(loading, item.id);
      if (data) {
        pushNotification("Successfully changed", "success");
        await getList();
      }
    } else if (status.value == "archive") {
      openArchiveModal(item);
    }
  }
};

const createNewTask = async () => {
  if (isSaving.value) return;
  if (
    !problems.value.length ||
    !newTask.value.company_id ||
    !newTask.value.driver_id ||
    !newTask.value.work_type_id
  ) {
    return;
  }

  try {
    if (newTask.value.company_name) {
      newTask.value.company_id = "";
      delete newTask.value.company_id;
    }

    if (newTask.value.driver_name) {
      newTask.value.driver_id = "";
      delete newTask.value.driver_id;
    }

    if (newTaskStatus.value == "new") {
      newTask.value.assignee_id = userStore.userProfile?.id as string;
      await taskStore.createTask(loading, newTask.value, (data: any) => {
        if (data) {
          if (Object.keys(data).length) {
            pushNotification("Successfully added", "success");
            getList();
          }
        }
      });
    } else {
      let result: any = { ...newTask.value };
      result.description = description.value;
      delete result.assignee_id;
      await taskStore.createInformTask(loading, result, (data: any) => {
        if (data) {
          if (Object.keys(data).length) {
            pushNotification("Successfully added", "success");
            getList();
            newTaskStatus.value = "new";
            description.value = "";
          }
        }
      });
    }
  } finally {
    isSaving.value = false;
  }
};

const createItems = (type: string) => {
  let allStatus: any[];

  if (type == "new") {
    allStatus = (
      Object.keys({
        INFORM: "inform",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  } else {
    allStatus = (
      Object.keys({
        NEW: "new",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  }

  return allStatus;
};

const dropdownSelect = (e: any) => {
  if (e.value === "inform") {
    newModal.value = true;
    description.value = "";
  } else {
    newTaskStatus.value = "new";
  }
};

const saveModal = async () => {
  newTaskStatus.value = "inform";
  newModal.value = false;
  newTask.value.work_type_id = "26317c42-8a8e-4666-b78a-eca83fbf1701";
  await nextTick();
};

const editModal = () => {
  newModal.value = true;
};

const refreshTaskWorkTypes = async (taskId: string) => {
  try {
    const { data } = await taskService.getTaskById(taskId);

    const index = tasks.value.findIndex((task) => task.id === taskId);

    if (index === -1) return;

    const current = tasks.value[index];

    tasks.value[index] = {
      ...current,
      ...data,
      isEditingProblem: current.isEditingProblem,
    };
  } catch (err: any) {
    pushNotification(
      err.response?.data?.detail || "Failed to refresh task",
      "error"
    );
  }
};

const updateProblem = async (item: ITaskData) => {
  if (!item.work_type_id) return;

  await taskStore.changeTaskWorkType(
    loading,
    item.id,
    { work_type_id: item.work_type_id },
    (data: any) => {
      if (data) {
        pushNotification("Successfully added", "success");
        getList();
      }
    }
  );
};

function enterFirst() {
  const el = dropdown2.value?.[0];

  if (!el) return;
  el.focus();
}

const enterSecond = async () => {
  const el = dropdown3.value?.[0];

  if (!el) return;
  el.focus();
};

const goLink = (item: any) => {
  window.open(item.eld_link, "_blank")?.focus();
};

let searchCompanyTimer: ReturnType<typeof setTimeout> | null = null;

const searchCompany = (val: string) => {
  companySearchVersion++;

  if (searchCompanyTimer) clearTimeout(searchCompanyTimer);

  searchCompanyTimer = setTimeout(() => {
    searchCompanyTimer = null;
    void fetchCompanyOptions(val);
  }, 300);
};

let searchDriverTimer: ReturnType<typeof setTimeout> | null = null;

const searchDriver = (val: string) => {
  driverSearchVersion++;

  if (searchDriverTimer) clearTimeout(searchDriverTimer);

  searchDriverTimer = setTimeout(() => {
    searchDriverTimer = null;
    void fetchDriverOptions(val);
  }, 300);
};

let searchProblemTimer: ReturnType<typeof setTimeout> | null = null;

const searchProblems = (val: string) => {
  problemSearchVersion++;

  if (searchProblemTimer) clearTimeout(searchProblemTimer);

  searchProblemTimer = setTimeout(() => {
    searchProblemTimer = null;
    void fetchProblemOptions(val);
  }, 300);
};

const openArchiveModal = (item: ITaskData) => {
  archiveModal.value = true;
  archiveReason.value = "";
  archiveModalItem.value = item;
};

const closeArchiveModal = () => {
  archiveModal.value = false;
  archiveReason.value = "";
  archiveModalItem.value = null;
};

const saveArchive = async () => {
  if (!archiveReason.value) return;
  if (!archiveModalItem.value) return;

  await archiveStore.archiveTask(
    loading,
    archiveModalItem.value.id,
    archiveReason.value,
    async (data: any) => {
      if (data) {
        pushNotification("Successfully archived", "success");
        closeArchiveModal();
        await getList();
      }
    }
  );
};

onMounted(async () => {
  companyStore.commonSimpleFilter.search = "";
  driverStore.commonSimpleFilter.search = "";
  workTypeStore.commonFilter.search = "";

  if (userStore.userProfile && userStore.userProfile.team_id) {
    companyStore.commonSimpleFilter.team_id = userStore.userProfile.team_id;
  }

  await getList();
  if (
    userStore.userProfile?.role === "admin" ||
    userStore.userProfile?.role === "super_admin"
  ) {
    isAdmin.value = true;
  }

  if (taskStore.myTaskFilter.search) {
    search.value = taskStore.myTaskFilter.search;
  }

  if (taskStore.myTaskFilter.status) {
    status.value = taskStore.myTaskFilter.status;
  }

  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    },
    { root: scrollContainer.value, rootMargin: "200px" }
  );
  if (sentinel.value) observer.observe(sentinel.value);
});

watch(search, (val) => {
  taskStore.myTaskFilter.search = val;

  taskFilterVersion++;

  if (searchTimer) {
    clearTimeout(searchTimer);
  }

  searchTimer = setTimeout(() => {
    searchTimer = null;
    void resetAndFetch();
  }, 300);
});

watch(status, (val) => {
  taskStore.myTaskFilter.status = val;

  if (searchTimer) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }

  void resetAndFetch();
});

watch(
  () => newTask.value.company_id,
  async (val) => {
    driverSearchVersion++;

    if (searchDriverTimer) {
      clearTimeout(searchDriverTimer);
      searchDriverTimer = null;
    }

    driverStore.commonSimpleFilter.search = "";

    const newVal = companies.value.some(
      (item) => item.id.toLowerCase() === val
    );

    drivers.value = [];
    newTask.value.driver_id = "";
    newTask.value.work_type_id = "";

    if (!newVal) {
      if (val) {
        newTask.value.company_name = val;
      } else {
        delete newTask.value.company_name;
      }

      driverStore.commonSimpleFilter.company_id = "";
      drivers.value = [];
    } else {
      delete newTask.value.company_name;

      if (val) {
        driverStore.commonSimpleFilter.company_id = val;
        await fetchDriverOptions("", val);
      }
    }
  }
);

watch(
  () => newTask.value.driver_id,
  (val) => {
    const newVal = drivers.value.some((item) => item.id.toLowerCase() === val);

    if (!newVal) {
      newTask.value.driver_name = val;
    }
  }
);

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);

onUnmounted(() => {
  observer?.disconnect();
  taskFilterVersion++;
  companySearchVersion++;
  driverSearchVersion++;
  problemSearchVersion++;
  kpiRequestVersion++;

  if (searchCompanyTimer) clearTimeout(searchCompanyTimer);
  if (searchDriverTimer) clearTimeout(searchDriverTimer);
  if (searchProblemTimer) clearTimeout(searchProblemTimer);
  if (searchTimer) clearTimeout(searchTimer);

  taskStore.clearMyTaskFilter();
  companyStore.clearSimpleFilter();
  driverStore.clearCommonSimpleFilter();
  workTypeStore.clearFilter();
});
</script>

<template>
  <div class="v-my-tasks" ref="scrollContainer">
    <section class="v-my-tasks__performance">
      <div class="v-my-tasks__performance-row">
        <div
          v-if="kpiLoading && !kpiStatistics"
          class="v-my-tasks__performance-loading"
        >
          Loading...
        </div>

        <template v-else-if="kpiStatistics">
          <div class="v-my-tasks__cards" :class="{ 'is-loading': kpiLoading }">
            <CustomCard
              v-for="card in summaryCards"
              :key="card.name"
              :class="`performance-${card.name}`"
              :label="card.label"
              :number="kpiStatistics[card.name]"
            />

            <CustomCard
              label="Closed %"
              :number="`${kpiStatistics.closedRate}%`"
            />
          </div>
        </template>
        <CustomTab v-model="performancePeriod" :tabs="performanceTabs" />
      </div>
    </section>

    <div class="v-my-tasks__header">
      <CommonSearch
        v-model="search"
        :icon="SearchMd"
        :placeholder="'Search'"
        clearable
      />
      <CommonDropdown
        v-model="status"
        :items="statusItems"
        label="Status"
        position="bottom"
        clearable
      />
      <CommonButton
        type="primary"
        :label="`New random task (${taskNumber})`"
        :prepend="true"
        :prependIcon="Clipboard"
        :loading="taskNumberLoading"
        @clicked="getNewTask"
      />
    </div>
    <div class="v-my-tasks__body">
      <template
        v-for="(dateGroup, dateIndex) in groupedByDate"
        :key="dateGroup.date"
      >
        <div
          class="v-my-tasks__date-row"
          @click="toggleDate(dateGroup.date)"
          :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
        >
          <span class="v-my-tasks__date-label">{{ dateGroup.date }}</span>
          <span
            class="v-my-tasks__date-chevron"
            :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
          >
            <ChevronDown :size="20" />
          </span>
        </div>

        <template v-if="!isDateCollapsed(dateGroup.date)">
          <CommonTable
            :headers="taskStore.taskHeadersUser"
            :items="dateGroup.items"
            :loading="loading"
            :isAdmin="isAdmin"
            isTaskTable
            :dateIndex="dateIndex"
          >
            <template #id="{ index }">
              <span>
                {{
                  dateGroup.items[index].status === "inform"
                    ? ""
                    : dateGroup.items
                        .slice(0, index)
                        .filter((item: any) => item.status !== "inform")
                        .length + 1
                }}
              </span>
            </template>

            <template #company="{ item }">
              <div class="company__name">
                <span class="title">
                  {{ item.company_name }}
                  <span
                    class="note__icon"
                    v-if="item.company_note"
                    v-tooltip="{
                      content: item.company_note,
                      html: true,
                    }"
                  >
                    <InfoCircle :size="14" />
                  </span>
                </span>
                <span class="app">{{
                  item.provider_name?.split(" ")[0] || "Hos"
                }}</span>
              </div>
            </template>

            <template #drivers="{ item }">
              <span class="v-users__badge driver__name">
                {{ item.driver_name }}
                <span
                  class="note__icon"
                  v-if="item.driver_note"
                  v-tooltip="{
                    content: item.driver_note,
                    html: true,
                  }"
                >
                  <InfoCircle :size="14" />
                </span>
              </span>
            </template>

            <template #date="{ item }">
              <span>{{ datepickerToUtcDateOnly(item.created_time) }}</span>
            </template>

            <template #status="{ item }">
              <CommonStatus
                v-if="item.status && getStatusEnumKey(item.status)"
                :status="getStatusEnumKey(item.status)!"
                @next="nextStatus(item)"
                @confirm="confirmStatus(item)"
                @selected="(e: any) => selectStatus(item, e)"
                :showActions="hasUserHasAction(item)"
                :items="availableStatusItems(item)"
                :tooltipContent="tooltipContent(item.qa_failed_reason_details)"
              />
              <span v-else>—</span>
            </template>

            <template #problems="{ item }">
              <div class="custom__problem--info" v-if="!item.isEditingProblem">
                <span class="title">
                  {{ item.description || item.work_type_name }}
                </span>
                <div
                  class="edit"
                  @click="item.isEditingProblem = true"
                  v-if="
                    hasUserHasAction(item) &&
                    item.status == 'in_progress' &&
                    item.source !== 'eld_import'
                  "
                >
                  <Pencil02 :size="16" />
                </div>
              </div>

              <div v-else class="custom__problem">
                <CustomTableDropdown
                  v-model="item.work_type_id"
                  :items="
                                      problems.map((p: any) => ({
                                        value: p.id,
                                        name: p.name,
                                      }))
                                    "
                  label="Problem"
                  position="bottom"
                  maxWidth="100%"
                  dropdownFullWidth
                  @search="searchProblems"
                />

                <div class="clear" @click="item.isEditingProblem = false">
                  <X :size="16" />
                </div>

                <div
                  class="problem__save"
                  :class="!item.work_type_id ? 'disabled' : ''"
                  @click="updateProblem(item)"
                >
                  Save
                </div>
              </div>

              <TaskAdditionalWorkTypes
                :task="item"
                :work-types="additionalWorkTypes"
                :can-edit="Boolean(hasUserHasAction(item))"
                @updated="refreshTaskWorkTypes(item.id)"
              />
            </template>

            <template #actions="{ item }">
              <div v-if="item.eld_link" class="icon">
                <LinkExternal02 :size="16" @click="goLink(item)" />
              </div>
            </template>

            <template #new-row>
              <td></td>
              <td class="td__dropdown">
                <CustomTableDropdown
                  ref="dropdown1"
                  v-model="newTask.company_id"
                  :items="companyItems"
                  label="Company"
                  position="bottom"
                  clearable
                  maxWidth="100%"
                  dropdownFullWidth
                  isAppendItem
                  @enter="enterFirst"
                  creatable
                  @search="searchCompany"
                />
              </td>

              <td class="td__dropdown">
                <CustomTableDropdown
                  ref="dropdown2"
                  v-model="newTask.driver_id"
                  :items="driverItems"
                  label="Driver"
                  position="bottom"
                  clearable
                  dropdownFullWidth
                  maxWidth="100%"
                  isAppendItem
                  @enter="enterSecond"
                  creatable
                  @search="searchDriver"
                />
              </td>

              <td>{{ datepickerToUtcDateOnly(new Date()) }}</td>
              <td>-</td>
              <td>
                <CommonStatus
                  :status="getStatusEnumKey(newTaskStatus)"
                  @next="console.log('Next driver-status')"
                  @confirm="console.log('Confirm driver-status')"
                  :items="createItems(newTaskStatus)"
                  @selected="dropdownSelect"
                  :arrow="false"
                  showActions
                />
              </td>
              <td class="td__dropdown">
                <div class="problem__group">
                  <CustomTableDropdown
                    ref="dropdown3"
                    v-model="newTask.work_type_id"
                    :items="problemItems"
                    label="Problem"
                    position="bottom"
                    clearable
                    dropdownFullWidth
                    isAppendItem
                    @enter="createNewTask"
                    @search="searchProblems"
                  />

                  <div
                    class="problem__group--edit"
                    v-if="newTaskStatus == 'inform'"
                    @click="editModal"
                  >
                    <Pencil02 :size="16" />
                  </div>

                  <div
                    class="problem__group--save"
                    :class="
                      isSaving ||
                      !problems.length ||
                      !newTask.company_id ||
                      !newTask.driver_id ||
                      !newTask.work_type_id
                        ? 'disabled'
                        : ''
                    "
                    @click="!isSaving && createNewTask()"
                  >
                    Save
                  </div>
                </div>
              </td>
            </template>
          </CommonTable>
        </template>
      </template>

      <div ref="sentinel" class="v-tasks__sentinel"></div>

      <div v-if="isLoadingMore" class="v-tasks__loading-more">
        Loading more…
      </div>
      <div
        v-else-if="!hasMore && tasks.length && !loading"
        class="v-tasks__end"
      >
        No more tasks
      </div>
    </div>

    <CommonModal v-model="newModal" title="Description">
      <div class="new-modal">
        <CommonSearch
          v-model="description"
          placeholder="Type here ..."
          width="100%"
        />
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton
            type="outline"
            label="Cancel"
            @clicked="newModal = false"
          />
          <CommonButton type="primary" label="Save" @clicked="saveModal" />
        </div>
      </template>
    </CommonModal>

    <CommonModal v-model="archiveModal" title="Archive reason">
      <div class="new-modal">
        <CommonInput v-model="archiveReason" label="Reason" multiline />
      </div>

      <div class="auto-complete">
        <div class="item" @click="archiveReason = 'Duplicate task'">
          Duplicate task
        </div>
        <div class="item" @click="archiveReason = 'Wrong name'">Wrong name</div>
        <div class="item" @click="archiveReason = 'Need to change status'">
          Need to change status
        </div>
        <div class="item" @click="archiveReason = 'Another worker doing'">
          Another worker doing
        </div>
        <div class="item" @click="archiveReason = 'Created by mistake'">
          Created by mistake
        </div>
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton
            type="outline"
            label="Cancel"
            @clicked="closeArchiveModal"
          />
          <CommonButton type="primary" label="Save" @clicked="saveArchive" />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-my-tasks {
  padding: var(--base-space-24);
  background: var(--color-system-background);
  height: calc(100vh - 80px);
  overflow: auto;

  &__performance {
    margin-top: -10px;
    margin-bottom: 12px;
    padding: 8px;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-secondary-system-background);
  }

  &__performance-row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    overflow-x: auto;
  }

  &__performance-row > :deep(.c-cu-tab) {
    flex: 0 0 auto;
    margin-left: auto;
    white-space: nowrap;
  }

  &__performance-row > :deep(.c-cu-tab .tab) {
    padding: 5px 9px;
  }

  &__performance-row > :deep(.c-cu-tab .tab__body) {
    font-size: 12px;
    line-height: 18px;
  }

  &__performance-loading {
    padding: 12px;
    color: var(--color-label-secondary);
    font-size: 12px;
  }

  &__cards {
    display: grid;
    grid-template-columns: repeat(8, minmax(95px, 1fr));
    gap: 6px;
    flex: 1;
    min-width: 0;
    transition: opacity 0.15s ease;

    &.is-loading {
      opacity: 0.55;
      pointer-events: none;
    }

    :deep(.c-cu-card) {
      min-width: 0;
      width: 100%;
      height: 52px;
      padding: 5px 8px;
      border-width: 1px;
      border-radius: 8px;
      background: var(--color-system-background);
    }

    // :deep(.c-cu-card.performance-in_progress) {
    //   background: var(--color-orange-light);
    // }

    // :deep(.c-cu-card.performance-on_hold) {
    //   background: var(--color-yellow-light);
    // }

    // :deep(.c-cu-card.performance-done) {
    //   background: var(--color-green-light);
    // }

    // :deep(.c-cu-card.performance-closed) {
    //   background: var(--color-green-light);
    // }

    // :deep(.c-cu-card.performance-qa_failed) {
    //   background: var(--color-red-light);
    // }

    // :deep(.c-cu-card.performance-in_progress),
    // :deep(.c-cu-card.performance-on_hold),
    // :deep(.c-cu-card.performance-done),
    // :deep(.c-cu-card.performance-closed),
    // :deep(.c-cu-card.performance-qa_failed) {
    //   border-color: transparent;
    // }

    :deep(.card__header__info--label) {
      font-size: 11px;
      line-height: 15px;
      margin-bottom: 2px;
      padding: 0;
      white-space: nowrap;
    }

    :deep(.card__header__info--number) {
      font-size: 18px;
      line-height: 22px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: var(--base-space-8);
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--base-space-4) var(--base-space-8);
    gap: var(--base-space-8);
    border-radius: var(--base-radius-8);
    color: var(--color-label);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;

    &.active {
      background-color: var(--color-green-light);
    }

    &.inactive {
      background-color: var(--color-red-light);
    }
  }

  &__actions {
    display: flex;
    gap: var(--base-space-8);
  }

  &__btn {
    cursor: pointer;
    transition: opacity 0.2s;
    background-color: var(--color-bg-secondary);
    color: var(--color-label);
    border-color: var(--color-border);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--base-space-8);
    width: 32px;
    height: 32px;
    padding: 6px;
    border-radius: var(--base-radius-12);

    &:hover {
      opacity: 0.8;
    }
  }

  &__date-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--base-space-8);
    padding: 10px 16px;
    cursor: pointer;
    user-select: none;
    border-top-left-radius: var(--base-radius-16);
    border-top-right-radius: var(--base-radius-16);
    transition: background-color 0.15s;
    background: var(--color-secondary-system-background);
    margin-top: 10px;
    height: 40px;

    &.is-collapsed {
      border-radius: var(--base-radius-16);
    }
  }

  &__date-chevron {
    display: flex;
    align-items: center;
    color: var(--color-label-secondary);
    transition: transform 0.2s;

    &.is-collapsed {
      transform: rotate(-180deg);
    }
  }

  &__date-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-label);
  }

  &__sentinel {
    height: 1px;
  }

  &__loading-more,
  &__end {
    text-align: center;
    padding: var(--base-space-16);
    font-size: 13px;
    color: var(--color-label-secondary);
  }

  :deep(.c-co-table) {
    margin-top: 0 !important;
  }

  :deep(.table thead th) {
    &:first-child {
      border-top-left-radius: 0 !important;
    }

    &:last-child {
      border-top-right-radius: 0 !important;
    }
  }

  .td__dropdown {
    padding: 0;

    .problem__group {
      display: flex;

      &--save,
      &--edit {
        margin: 8px 12px;
        background: var(--color-secondary-system-background);
        border-radius: var(--base-radius-8);
        padding: 4px 8px;
        color: var(--color-primary);
        font-size: 14px;
        line-height: 20px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;

        &.disabled {
          cursor: not-allowed;
          pointer-events: none;
        }
      }

      &--edit {
        margin: 8px 0 8px 12px;
      }
    }
  }

  .custom__problem {
    display: flex;
    align-items: center;

    .problem__save {
      background: var(--color-secondary-system-background);
      border-radius: var(--base-radius-8);
      padding: 4px 8px;
      color: var(--color-primary);
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;

      &.disabled {
        cursor: not-allowed;
        pointer-events: none;
      }
    }

    .clear {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      margin-right: 4px;
    }
  }

  .custom__problem--info {
    display: flex;
    align-items: center;

    .title {
      flex: 1;
    }

    .edit {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: var(--color-secondary-system-background);
      border-radius: var(--base-radius-8);
      padding: 4px 8px;
      color: var(--color-primary);
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
    }
  }
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}

.company__name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;

  .title {
    display: flex;
    align-items: center;

    .note__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      margin-left: 8px;
      color: var(--color-primary);
    }
  }

  .app {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: var(--color-label-secondary);
    white-space: nowrap;
  }
}

.driver__name {
  display: flex;
  align-items: center;

  .note__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    margin-left: 8px;
    color: var(--color-primary);
  }
}

.auto-complete {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: var(--base-space-8);
  flex-wrap: wrap;

  .item {
    cursor: pointer;
    border: 1px solid var(--color-border);
    color: var(--color-label);
    padding: 10px 14px;
    border-radius: 14px;
    background: var(--color-secondary-system-background);
  }
}
</style>
